import React from "react";
import { Button } from "./ui/button";
import { ArrowRight, Heart, Users, Award } from "lucide-react";
import { mockData } from "../data/mockData";

const HeroSection = ({ data }) => {
  const testimonials = data?.testimonials || [];
  const stats = data?.companyInfo?.stats || [];
  const company = data?.companyInfo?.company || {};
  
  const firstTestimonial = testimonials.length > 0 ? testimonials[0] : null;
  const scrollToContact = () => {
    const element = document.querySelector("#contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToAbout = () => {
    const element = document.querySelector("#about");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="home" className="min-h-screen bg-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900/50 to-black"></div>
      
      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-2 h-2 bg-red-500 rounded-full animate-pulse opacity-60"></div>
      <div className="absolute top-40 right-20 w-1 h-1 bg-white rounded-full animate-pulse opacity-40"></div>
      <div className="absolute bottom-40 left-20 w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse opacity-50"></div>

      <div className="relative z-10 container mx-auto px-6 pt-32 pb-20">
        <div className="max-w-6xl mx-auto">
          
          {/* Animated Keywords */}
          <div className="mb-8 flex flex-wrap gap-4 justify-center opacity-60">
            {["•Wellness•", "•Independence•", "•Community•", "•Mobility•", "•Vitality•", "•Care•"].map((keyword, index) => (
              <span
                key={keyword}
                className="text-sm text-white/60 animate-pulse"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                {keyword}
              </span>
            ))}
          </div>

          {/* Main Headline */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-light leading-tight mb-6">
              <span className="block animate-fade-in-up">We put life in your years</span>
            </h1>
            
            <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-12 animate-fade-in-up animation-delay-900 font-light">
              HELPING YOU STAY INDEPENDENT & LIVE LIFE THE WAY YOU WANT TO
            </p>
          </div>

          {/* About Preview */}
          <div className="max-w-4xl mx-auto text-center mb-16 animate-fade-in-up animation-delay-1200">
            <h2 className="text-2xl md:text-3xl font-light mb-6">We're <span className="text-white">reignite</span><span className="text-orange-500">health</span><span className="text-red-500">.</span></h2>
            
            {/* Video Integration */}
            <div className="mb-12">
              <div className="relative rounded-lg overflow-hidden max-w-2xl mx-auto bg-gray-900/50 border border-white/10">
                <video 
                  className="w-full h-auto"
                  controls
                  preload="metadata"
                  poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='640' height='360' viewBox='0 0 640 360'%3E%3Crect width='640' height='360' fill='%23111111'/%3E%3Ctext x='320' y='180' text-anchor='middle' fill='%23ffffff' font-size='18' font-family='Arial'%3ECan you tell our client is a good dancer?!%3C/text%3E%3C/svg%3E"
                >
                  <source 
                    src="https://customer-assets.emergentagent.com/job_senior-living-plus/artifacts/m4lyxbuq_IMG_0779.mov" 
                    type="video/mp4"
                  />
                  <source 
                    src="https://customer-assets.emergentagent.com/job_senior-living-plus/artifacts/m4lyxbuq_IMG_0779.mov" 
                    type="video/quicktime"
                  />
                  Your browser does not support the video tag.
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none"></div>
              </div>
              <p className="text-sm text-white/60 mt-4 font-light">
                See our team in action - building genuine connections and transforming lives
              </p>
            </div>

            <p className="text-lg text-white/80 leading-relaxed mb-8 font-light">
              We help you stay independent, living in your retirement village, and continuing to do the things you love. 
              Whether it's playing bowls, meeting friends for coffee, picking up your grandchildren, or dancing 
              at their weddings - we're here to put life in your years.
            </p>
            <p className="text-base text-white/60 mb-8">
              From our physiotherapy sessions to your bowling green, from our balance classes to your grandchildren's 
              arms - we create the strength and confidence you need to keep living your life to the fullest.
            </p>
          </div>

          {/* Stats Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 animate-fade-in-up animation-delay-1500">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-full mb-4 group-hover:bg-red-500/20 transition-all duration-300">
                <Heart className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-light mb-2">Specialised Care</h3>
              <p className="text-white/60 text-sm">Exclusively serving retirement villages</p>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-full mb-4 group-hover:bg-red-500/20 transition-all duration-300">
                <Users className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-light mb-2">Community Focus</h3>
              <p className="text-white/60 text-sm">Building relationships and connections</p>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-full mb-4 group-hover:bg-red-500/20 transition-all duration-300">
                <Award className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-light mb-2">Market Leader</h3>
              <p className="text-white/60 text-sm">Leading innovation in retirement care</p>
            </div>
          </div>

          {/* Quick Testimonial Preview */}
          {firstTestimonial && (
            <div className="mb-16 animate-fade-in-up animation-delay-1600">
              <div className="bg-gray-900/40 rounded-lg p-8 border border-white/10 max-w-4xl mx-auto">
                <div className="text-center">
                  <div className="flex text-orange-500 justify-center mb-4">
                    {[...Array(firstTestimonial.rating)].map((_, i) => (
                      <span key={i} className="text-lg">★</span>
                    ))}
                  </div>
                  <p className="text-xl text-white/90 italic leading-relaxed font-light mb-6">
                    "{firstTestimonial.quote}"
                  </p>
                  <div className="flex items-center justify-center space-x-4">
                    <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                      <span className="text-orange-500 font-bold">{firstTestimonial.name.charAt(0)}</span>
                    </div>
                    <div className="text-left">
                      <p className="text-white font-light text-sm">{firstTestimonial.name}{firstTestimonial.age ? `, ${firstTestimonial.age}` : ''}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up animation-delay-1800">
            <Button
              onClick={() => {
                const element = document.querySelector("#differently");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-sm font-light text-base transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-red-500/25"
            >
              Discover Our Difference
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button
              onClick={() => {
                const element = document.querySelector("#testimonials");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
              variant="outline"
              className="border-white/30 text-white hover:bg-white hover:text-black px-8 py-3 rounded-sm font-light text-base transition-all duration-300 hover:scale-105"
            >
              Read Success Stories
            </Button>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-px h-8 bg-gradient-to-b from-transparent via-white/40 to-transparent"></div>
      </div>
    </section>
  );
};

export default HeroSection;